import { Component, Input } from '@angular/core';
import { openClose } from '@core/animations/animations';
import {
	InsuranceAmountOptions,
	InterestCalculatedOptions,
	InterestRatePrimeOptions,
	InterestRateTypeDescription,
	InterestRateTypeOptions,
	PaymentFrequencyOptions,
	getInterestRateDescription,
	getAsRegInterestRate,
	InterestCalculatedOptionsVTB,
} from '@core/util/mortgage';
import {
	InsuranceAmount,
	InterestRateType,
	MatterMortgageType,
	MatterMortgageWithAssociations,
	MortgageInterestCalculations,
	MortgagePaymentFrequencies,
	TransactionType,
} from '@models/mortgage';
import {
	MatterMortgageDispatch,
	MatterMortgagesState,
} from '@state/matter/matter_mortgages';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { ICON } from '@core/util/icon';
import { NgForm } from '@angular/forms';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { NavigationService } from '@services/nav/nav.service';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterFileType } from '@models/matter';
import { Observable } from 'rxjs';
import { Select } from '@ngxs/store';

@Component({
	selector: 'app-matter-mortgage-payments',
	templateUrl: './payments.component.html',
	animations: [openClose()],
	standalone: false,
})
export class MatterMortgagePaymentsComponent {
	public ICON = ICON;
	public insuranceAmountOptions = InsuranceAmountOptions;
	public interestRateTypeOptions = InterestRateTypeOptions;
	public interestCalculatedOptions = InterestCalculatedOptions;
	public interestCalculatedOptionsVTB = InterestCalculatedOptionsVTB;
	public interestRatePrimeOptions = InterestRatePrimeOptions;
	public paymentFrequencyOptions = PaymentFrequencyOptions;

	public _mortgage: MatterMortgageWithAssociations;
	public registeredEditable = false;
	public variableRateType = InterestRateType.VARIABLE;
	public interestRateText: string;
	public interestRateAsRegPlaceholder: string;
	public MATTER_MORTGAGE_TYPE = MatterMortgageType;
	public forceErrorState = false;
	public transactionTypes = TransactionType;
	@SelectSnapshot(MatterMortgagesState.getAmortizationScheduleDocHashedId)
	getAmoritzationScheduleDocIdFn: (mortgageId: string) => string;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(SelectedMatter.matterFileType) fileType$: Observable<MatterFileType>;
	public fileTypes = MatterFileType;
	@Input() set mortgage(mortgage: MatterMortgageWithAssociations) {
		this._mortgage = mortgage;
		this.interestRateAsRegPlaceholder = getAsRegInterestRate(mortgage);
		this.interestRateText = getInterestRateDescription(
			mortgage?.interestRate,
			mortgage?.interestRateType,
			mortgage?.interestAbovePrime,
		);
		this.registeredEditable = mortgage?.type !== MatterMortgageType.LLC;
	}

	constructor(
		private mortgageDispatch: MatterMortgageDispatch,
		private navigationService: NavigationService,
	) {}

	get interestRateTypePlaceHolder() {
		return InterestRateTypeDescription[this._mortgage?.interestRateType] ?? '';
	}

	setTerm(value: string): void {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, { term: value });
	}

	setAmortization(value: string): void {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			amortization: value,
		});
	}

	setAmount(value: number): void {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, { amount: value });
	}

	setLoanAmount(value: number): void {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			loanAmount: value,
		});
	}

	setStandardChargeTerm(value: string): void {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			standardChargeTerm: value,
		});
	}

	setInsuranceAmount(value: InsuranceAmount): void {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			insuranceAmount: value,
		});
	}

	setInterestOnlyPayments(value: boolean): void {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			interestOnlyPayments: value,
		});
	}

	setFirstPaymentDateAsReg(value: string) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			firstPaymentDateAsReg: value,
		});
	}

	setFirstPaymentDate(value: string) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			firstPaymentDate: value,
		});
	}

	setPaymentDueAsReg(value: string) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			paymentDueAsReg: value,
		});
	}

	setPaymentDue(value: string) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			paymentDue: value,
		});
	}

	setLastPaymentDateAsReg(value: string) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			lastPaymentDateAsReg: value,
		});
	}

	setLastPaymentDate(value: string) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			lastPaymentDate: value,
		});
	}

	setMaturityDateAsReg(value: string) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			maturityDateAsReg: value,
		});
	}

	setMaturityDate(value: string) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			maturityDate: value,
		});
	}

	setInterestAdjustmentDateAsReg(value: string) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			interestAdjustmentDateAsReg: value,
		});
	}

	setInterestAdjustmentDate(value: string) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			interestAdjustmentDate: value,
		});
	}

	setPaymentFrequencyAsReg(value: string) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			paymentFrequencyAsReg: value,
		});
	}

	setPaymentFrequency(value: MortgagePaymentFrequencies) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			paymentFrequency: value,
		});
	}

	setRegularPaymentsAsReg(value: string) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			regularPaymentsAsReg: value,
		});
	}

	selectPaymentFrequency(
		value: DropdownResult<DropdownOption<MortgagePaymentFrequencies>>,
	) {
		if (value?.result?.value) {
			this.setPaymentFrequency(value.result.value);
		}
	}

	selectPaymentFrequencyAsReg(value: DropdownResult<DropdownOption<string>>) {
		if (value?.result?.value) {
			this.setPaymentFrequencyAsReg(value.result.value);
		}
	}

	setRegularPayments(value: number) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			regularPayments: value,
		});
	}

	setTaxesPaidViaMortgage(value: boolean) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			lenderCollectsPropertyTaxes: value,
		});
	}

	setTaxes(value: number) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, { taxes: value });
	}

	setOther(value: number) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, { other: value });
	}

	setPrincipalAndInterest(value: number) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			principalAndInterest: value,
		});
	}

	setInterestCalculated(value: MortgageInterestCalculations) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			interestCalculated: value,
		});
	}

	selectInterestCalculated(
		value: DropdownResult<DropdownOption<MortgageInterestCalculations>>,
	) {
		if (value?.result?.value) {
			this.setInterestCalculated(value.result.value);
		}
	}

	setInterestCalculatedAsReg(value: string) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			interestCalculatedAsReg: value,
		});
	}

	selectInterestCalculatedAsReg(value: DropdownResult<DropdownOption<string>>) {
		if (value?.result?.value) {
			this.setInterestCalculatedAsReg(value.result.value);
		}
	}

	setInterestRateType(value: InterestRateType) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			interestRateType: value,
		});
	}

	setInterestRateTypeAsReg(value: string) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			interestRateTypeAsReg: value,
		});
	}

	setPrimeInterestRate(value: number) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			primeInterestRate: value,
		});
	}

	setInterestRate(value: number) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			interestRate: value,
		});
	}

	setInterestRateAsReg(value: string) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			interestRateAsReg: value,
		});
	}

	setAbovePrime(value: boolean) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			interestAbovePrime: value,
		});
	}

	setAdditionalClauses(value: string): void {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			additionClauses: value,
		});
	}

	viewAmortizationScheduleDocument(form: NgForm) {
		if (form.valid && this._mortgage?.amortization && this._mortgage?.term) {
			this.navigationService.goToMatter_Documents_Doc(
				this.matterId,
				this.getAmoritzationScheduleDocIdFn(this._mortgage.id),
			);
		} else {
			this.forceErrorState = true;
		}
	}
}
